import { forwardRef } from "react";
import { cn } from "../lib/cn";
import { CloseIcon } from "../lib/icons";

export interface ChipProps extends React.HTMLAttributes<HTMLDivElement> {
  color?:
    | "default"
    | "primary"
    | "secondary"
    | "success"
    | "warning"
    | "error"
    | "info";
  variant?: "filled" | "outlined";
  size?: "sm" | "md";
  onDelete?: React.EventHandler<React.MouseEvent>;
  onClick?: React.EventHandler<React.MouseEvent>;
  icon?: React.ReactNode;
  deleteIcon?: React.ReactNode;
  label: React.ReactNode;
  /** Dims the chip and blocks click/delete actions. */
  disabled?: boolean;
}

type ChipColor = NonNullable<ChipProps["color"]>;
type ChipSize = NonNullable<ChipProps["size"]>;

/**
 * Filled chips use quiet backgrounds — full status fills are reserved for
 * Alert. Status colours apply a 12% tint plus status-toned label text so
 * the chip reads crisply on cards in both modes.
 */
const filledColorClasses: Record<ChipColor, string> = {
  default: "bg-surface-selected text-content-primary",
  primary: "bg-surface-selected text-content-primary",
  secondary: "bg-surface-inverse text-on-inverse",
  success: "bg-surface-success-subtle text-content-success",
  warning: "bg-surface-warning-subtle text-content-warning",
  error: "bg-surface-error-subtle text-content-error",
  info: "bg-surface-info-subtle text-content-info",
};

/**
 * Outlined chips carry a real hairline so they read as crisp outline chips,
 * not default-y colourless pills. Status colours stroke + tone the label;
 * default/secondary stay neutral with a border-strong hairline.
 */
const outlinedColorClasses: Record<ChipColor, string> = {
  default: "border border-border-strong text-content-secondary",
  primary: "border border-border-primary-strong text-content-primary",
  secondary: "border border-border-strong text-content-primary",
  success: "border border-border-success-strong text-content-success",
  warning: "border border-border-warning-strong text-content-warning",
  error: "border border-border-error-strong text-content-error",
  info: "border border-border-info-strong text-content-info",
};

const sizeClasses: Record<ChipSize, string> = {
  sm: "h-5 px-2",
  md: "h-6 px-2.5",
};

/** Sizes any leading svg icon passed via `icon`. */
const iconSizeClasses: Record<ChipSize, string> = {
  sm: "[&_svg]:size-3",
  md: "[&_svg]:size-3.5",
};

/** Sizes the delete affordance and its svg. */
const deleteSizeClasses: Record<ChipSize, string> = {
  sm: "size-3.5 [&_svg]:size-2.5",
  md: "size-4 [&_svg]:size-3",
};

interface ChipDeleteButtonProps {
  size: ChipSize;
  onDelete: React.EventHandler<React.MouseEvent>;
  deleteIcon?: React.ReactNode;
  disabled?: boolean;
}

function ChipDeleteButton({
  size,
  onDelete,
  deleteIcon,
  disabled,
}: ChipDeleteButtonProps) {
  const handleDelete = (event: React.MouseEvent<HTMLButtonElement>) => {
    event.stopPropagation();
    onDelete(event);
  };

  return (
    <button
      type="button"
      aria-label="Remove"
      disabled={disabled}
      onClick={disabled ? undefined : handleDelete}
      className={cn(
        "flex shrink-0 items-center justify-center rounded-full transition-opacity focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-border-focused",
        disabled ? "cursor-not-allowed" : "hover:opacity-60",
        deleteSizeClasses[size],
      )}
    >
      {deleteIcon ?? <CloseIcon aria-hidden="true" />}
    </button>
  );
}

/**
 * Compact tag (md = 24px tall). Filled variants stay quiet — status fills
 * belong to Alert. Pass `onDelete` for a removable chip with an inline ×.
 */
export const Chip = forwardRef<HTMLDivElement, ChipProps>(
  (
    {
      color = "default",
      variant = "filled",
      size = "md",
      onDelete,
      onClick,
      icon,
      deleteIcon,
      label,
      disabled = false,
      onKeyDown,
      className,
      ...props
    },
    ref,
  ) => {
    const clickable = Boolean(onClick) && !disabled;
    const colorClasses =
      variant === "outlined"
        ? outlinedColorClasses[color]
        : filledColorClasses[color];

    const handleKeyDown = (event: React.KeyboardEvent<HTMLDivElement>) => {
      if (onKeyDown) {
        onKeyDown(event);
      }
      if (event.defaultPrevented || !clickable) {
        return;
      }
      if (event.key === "Enter" || event.key === " ") {
        event.preventDefault();
        // Fires a real click so the consumer's onClick receives a MouseEvent.
        event.currentTarget.click();
      }
    };

    return (
      <div
        ref={ref}
        data-slot="chip"
        data-variant={variant}
        data-size={size}
        data-color={color}
        role={clickable ? "button" : undefined}
        tabIndex={clickable ? 0 : undefined}
        aria-disabled={disabled || undefined}
        onClick={disabled ? undefined : onClick}
        onKeyDown={handleKeyDown}
        className={cn(
          "inline-flex select-none items-center gap-1 whitespace-nowrap rounded-full type-caption font-medium",
          colorClasses,
          sizeClasses[size],
          disabled && "cursor-not-allowed opacity-38",
          clickable &&
            "cursor-pointer focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-border-focused",
          className,
        )}
        {...props}
      >
        {icon ? (
          <span
            className={cn("flex shrink-0 items-center", iconSizeClasses[size])}
          >
            {icon}
          </span>
        ) : null}
        <span className="truncate">{label}</span>
        {onDelete ? (
          <ChipDeleteButton
            size={size}
            onDelete={onDelete}
            deleteIcon={deleteIcon}
            disabled={disabled}
          />
        ) : null}
      </div>
    );
  },
);
Chip.displayName = "Chip";
